/** Min height drop (px) / share of the stable height that we treat as an on-screen keyboard. */
export const KEYBOARD_MIN_DELTA_PX = 120
export const KEYBOARD_MIN_DELTA_RATIO = 0.15

/** Input types that don't bring up the text keyboard. */
const NON_TEXT_INPUT_TYPES = new Set([
    'checkbox',
    'radio',
    'button',
    'submit',
    'reset',
    'range',
    'file',
    'color',
    'image',
    'hidden',
])

/** An element that brings up the on-screen keyboard when focused. */
export function isEditableElement(el: Element | null): boolean {
    if (!el) return false
    if (el.getAttribute('inputmode') === 'none') return false
    if (el instanceof HTMLInputElement) {
        return !NON_TEXT_INPUT_TYPES.has(el.type) && !el.disabled && !el.readOnly
    }
    if (el instanceof HTMLTextAreaElement) {
        return !el.disabled && !el.readOnly
    }
    const contentEditable = el.getAttribute('contenteditable')
    return contentEditable === '' || contentEditable === 'true'
}

export interface KeyboardLockState {
    width: number | null
    stableHeight: number | null
    editableFocused: boolean
}

export type KeyboardLockEvent =
    | { type: 'resize'; width: number; height: number }
    | { type: 'focusin'; editable: boolean }
    /** editable = isEditableElement(document.activeElement) after a tick (focus moved field → field). */
    | { type: 'focusout-settled'; editable: boolean }

export const initialKeyboardLockState: KeyboardLockState = {
    width: null,
    stableHeight: null,
    editableFocused: false,
}

export function reduceKeyboardLock(state: KeyboardLockState, event: KeyboardLockEvent): KeyboardLockState {
    switch (event.type) {
        case 'resize': {
            if (state.width === null || !state.editableFocused) {
                // Outside focus every height is "full" (browser toolbars included).
                return { ...state, width: event.width, stableHeight: event.height }
            }
            if (event.width !== state.width) {
                // Rotated with the keyboard open: the remembered height no longer applies.
                return { ...state, width: event.width, stableHeight: null }
            }
            // Focused: the keyboard may only shrink the viewport — keep the full height, allow growth.
            return state.stableHeight === null
                ? state
                : { ...state, stableHeight: Math.max(state.stableHeight, event.height) }
        }
        case 'focusin':
            return event.editable ? { ...state, editableFocused: true } : state
        case 'focusout-settled':
            return { ...state, editableFocused: event.editable }
        default:
            return state
    }
}

/** Height (px) to pin the app root to, or null when no lock applies. */
export function getLockHeight(state: KeyboardLockState, coarsePointer: boolean): number | null {
    return state.editableFocused && coarsePointer && state.stableHeight !== null ? state.stableHeight : null
}

export function isKeyboardLikelyOpen(stableHeight: number | null, currentHeight: number): boolean {
    if (stableHeight === null) return false
    const threshold = Math.max(KEYBOARD_MIN_DELTA_PX, KEYBOARD_MIN_DELTA_RATIO * stableHeight)
    return stableHeight - currentHeight >= threshold
}
